/* ============================================================================
   MALL DIRECTORY — section behaviour
   Theme: Shopify Prestige (works on any OS 2.0 theme)
   Paired with: sections/mall-directory.liquid, assets/mall-directory.css

   Responsibilities
     · GSAP hero timeline, scroll reveals, parallax, stat counters
     · Marquees (announcement bar + store ribbon)
     · Countdown clocks
     · Quick-view dialog, populated from data- attributes on each card
     · Sticky chip nav with scroll spy
     · Theme editor lifecycle (re-init on block add / move / select)

   Design rules
     · Nothing is hidden by CSS. GSAP supplies its own "from" state, so if the
       CDN is blocked or the visitor prefers reduced motion the page is simply
       static and fully readable.
     · Every instance is scoped to its own section root, so the section can be
       added to a page more than once without the instances fighting.
   ============================================================================ */

(function () {
  'use strict';

  var GS = window.gsap;
  var ST = window.ScrollTrigger;
  var HAS_GSAP = !!(GS && ST);
  if (HAS_GSAP) GS.registerPlugin(ST);

  /* Every live instance, keyed by section id, so the theme editor can tear one
     down and rebuild it without touching the others. */
  var instances = {};

  function pad2(n) { return String(n).padStart(2, '0'); }

  /* ------------------------------------------------------------- INSTANCE */
  function MallSection(root) {
    this.root = root;
    this.id = root.dataset.sectionId;
    this.marquees = [];
    this.heroTriggers = [];
    this.contentTriggers = [];
    this.timers = [];
    this.allowMotion = false;
    this.mm = null;
    this.init();
  }

  MallSection.prototype.$ = function (sel) { return this.root.querySelector(sel); };
  MallSection.prototype.$$ = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  MallSection.prototype.init = function () {
    this.buildMarquees();
    this.startClocks();
    this.bindQuickView();
    this.bindStoreModal();
    this.bindChips();
    this.bindBackToTop();
    this.bindFaq();
    this.measureBars();
    this.updateProgress();

    var self = this;
    this.onResize = function () { self.measureBars(); self.layoutMarquees(); if (HAS_GSAP) ST.refresh(); };
    window.addEventListener('resize', this.onResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.onResize);

    if (HAS_GSAP && this.root.dataset.animate === 'true') this.startMotion();
    else this.layoutMarquees();   /* marquees still need their clones sized */
  };

  /* ------------------------------------------------------------- TEARDOWN */
  MallSection.prototype.destroy = function () {
    window.removeEventListener('resize', this.onResize);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.timers.forEach(clearInterval);
    this.timers = [];
    this.killTriggers(this.heroTriggers);
    this.killTriggers(this.contentTriggers);
    this.marquees.forEach(function (m) { if (m.tween) m.tween.kill(); });
    this.marquees = [];
    if (this.mm) { this.mm.revert(); this.mm = null; }
  };

  MallSection.prototype.killTriggers = function (list) {
    list.forEach(function (t) { if (t && t.kill) t.kill(); });
    list.length = 0;
  };

  /* ---------------------------------------------------------- STICKY BARS
     The chip bar pins under whatever sticky header the theme provides, and the
     floor headings pin under the chip bar. Both offsets are measured live so
     this works on any theme without hard-coded numbers. */
  MallSection.prototype.measureBars = function () {
    var header = document.querySelector(
      '[class*="shopify-section-header"], [class*="section-header"], .site-header, #SiteHeader, .header-wrapper, sticky-header, .shopify-section-group-header-group'
    );
    var top = 0;
    if (header) {
      var pos = getComputedStyle(header).position;
      if (pos === 'sticky' || pos === 'fixed') top = Math.round(header.getBoundingClientRect().height);
    }
    var chips = this.$('.hp-chips');
    this.root.style.setProperty('--topbar-h', top + 'px');
    if (chips) {
      chips.style.top = top + 'px';
      this.root.style.setProperty('--bars-h', (top + chips.offsetHeight) + 'px');
    } else {
      this.root.style.setProperty('--bars-h', top + 'px');
    }
  };

  /* ------------------------------------------------------------- MARQUEES
     A track is filled with repeated sets so the loop is seamless at any width,
     then GSAP slides it by exactly one set width, forever. */
  MallSection.prototype.buildMarquees = function () {
    var self = this;
    this.$$('[data-marquee]').forEach(function (track) {
      var html = track.innerHTML.trim();
      if (!html) return;
      track.innerHTML = '<div class="hp-set">' + html + '</div>';
      self.marquees.push({
        track: track,
        html: html,
        speed: parseFloat(track.dataset.speed) || 42,
        tween: null
      });
    });
  };

  MallSection.prototype.layoutMarquees = function () {
    var self = this;
    this.marquees.forEach(function (m) {
      var setWidth = m.track.firstElementChild.offsetWidth || 1;
      /* Cap the clones. A zero measurement (fonts loading, hidden tab) would
         otherwise ask for thousands of copies and lock the page up. */
      var need = Math.min(12, Math.max(2, Math.ceil((window.innerWidth * 2) / setWidth)));
      while (m.track.children.length < need) {
        m.track.insertAdjacentHTML('beforeend', '<div class="hp-set">' + m.html + '</div>');
      }
      if (m.tween) { m.tween.kill(); m.tween = null; }
      if (!self.allowMotion || !HAS_GSAP) return;
      GS.set(m.track, { x: 0 });
      m.tween = GS.to(m.track, { x: -setWidth, duration: setWidth / m.speed, ease: 'none', repeat: -1 });
      var host = m.track.parentElement;
      host.addEventListener('mouseenter', function () { if (m.tween) m.tween.pause(); });
      host.addEventListener('mouseleave', function () { if (m.tween) m.tween.resume(); });
    });
  };

  /* --------------------------------------------------------------- CLOCKS
     Each clock counts down to its own data-deadline (an ISO string rendered by
     Liquid). When it passes, it rolls forward a day so a daily deal keeps
     working without the merchant editing anything. */
  MallSection.prototype.startClocks = function () {
    var clocks = this.$$('[data-deadline]');
    if (!clocks.length) return;
    function tick() {
      clocks.forEach(function (c) {
        var end = new Date(c.dataset.deadline);
        if (isNaN(end)) return;
        var now = new Date();
        while (end <= now) end.setDate(end.getDate() + 1);
        var left = Math.floor((end - now) / 1000);
        var h = c.querySelector('[data-h]'), m = c.querySelector('[data-m]'), s = c.querySelector('[data-s]');
        if (h) h.textContent = pad2(Math.floor(left / 3600));
        if (m) m.textContent = pad2(Math.floor((left % 3600) / 60));
        if (s) s.textContent = pad2(left % 60);
      });
    }
    tick();
    this.timers.push(setInterval(tick, 1000));
  };

  /* ----------------------------------------------------------- QUICK VIEW
     Card markup carries everything the dialog needs in data- attributes, so
     opening it costs no network request. */
  MallSection.prototype.bindQuickView = function () {
    var self = this;
    var sheet = this.$('[data-sheet]');
    if (!sheet) return;
    var panel = sheet.querySelector('[data-sheet-panel]');

    this.root.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-quickview]');
      if (!opener) return;
      e.preventDefault();
      self.openSheet(sheet, panel, opener);
    });
    sheet.addEventListener('click', function (e) { if (e.target === sheet) sheet.close(); });
  };

  MallSection.prototype.openSheet = function (sheet, panel, card) {
    var d = card.dataset;
    var esc = function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    };
    var img = d.image
      ? '<span class="hp-ph"><span>' + esc((d.store || 'H').charAt(0)) + '</span></span>' +
        '<img src="' + esc(d.image) + '" alt="' + esc(d.title) + '" onerror="this.remove()">'
      : '<span class="hp-ph"><span>' + esc((d.store || 'H').charAt(0)) + '</span></span>';

    panel.setAttribute('style', '--tone:' + (d.tone || '#7C3AED') + ';--tone-t:' + (d.toneTint || '#F3EEFF'));
    panel.innerHTML =
      '<div class="hp-handle"></div>' +
      '<button type="button" class="hp-close" aria-label="Close">' +
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<div class="hp-body">' +
        '<div class="hp-img">' + img + '</div>' +
        '<div>' +
          (d.store ? '<p class="hp-prefix">' + esc(d.store) + (d.floor ? ' &middot; ' + esc(d.floor) : '') + '</p>' : '') +
          '<h2 id="hp-sheet-title">' + esc(d.title) + '</h2>' +
          '<div class="hp-price"><b class="hp-num">' + esc(d.price) + '</b>' +
            (d.compare ? '<s class="hp-num">' + esc(d.compare) + '</s>' : '') +
            (d.off && +d.off > 0 ? '<span class="hp-off">' + esc(d.off) + '% off</span>' : '') +
          '</div>' +
          (d.saved ? '<span class="hp-saved">You save ' + esc(d.saved) + ' on the MRP</span>' : '') +
          (d.desc ? '<div class="hp-desc">' + d.desc + '</div>' : '') +
          (d.unit || d.floor
            ? '<div class="hp-locate">' +
                (d.unit ? '<span class="hp-unit">' + esc(d.unit) + '</span>' : '') +
                '<span class="hp-t">' + esc(d.store || '') + (d.floor ? ', ' + esc(d.floor) : '') +
                  '<small>Show this screen at the counter for the same price in store</small></span>' +
              '</div>'
            : '') +
          '<div class="hp-actions">' +
            (d.available === 'true'
              ? '<a class="hp-primary" href="' + esc(d.cartUrl || d.url) + '">Buy now</a>'
              : '<span class="hp-primary" style="opacity:.55">Sold out</span>') +
            '<a class="hp-secondary" href="' + esc(d.url) + '">View full details</a>' +
            (d.collectionUrl
              ? '<a class="hp-secondary hp-full" href="' + esc(d.collectionUrl) + '">All deals from ' + esc(d.store || 'this store') + '</a>'
              : '') +
          '</div>' +
        '</div>' +
      '</div>';

    panel.querySelector('.hp-close').addEventListener('click', function () { sheet.close(); });

    if (sheet.open) { panel.scrollTop = 0; return; }
    if (typeof sheet.showModal === 'function') sheet.showModal();
    else sheet.setAttribute('open', '');

    if (HAS_GSAP && this.allowMotion) {
      GS.from(panel, { y: 34, autoAlpha: 0, duration: .45, ease: 'power3.out' });
      GS.from(panel.querySelectorAll('.hp-body > div > *'), { y: 16, autoAlpha: 0, duration: .5, stagger: .045, delay: .1 });
    }
  };

  /* ----------------------------------------------------------- STORE MODAL */
  MallSection.prototype.bindStoreModal = function () {
    var self = this;
    var modal = this.$('[data-store-modal]');
    if (!modal) return;
    var panel = modal.querySelector('[data-store-panel]');
    var closeBtn = modal.querySelector('[data-store-close]');
    var headUnit = modal.querySelector('[data-store-head-unit]');
    var headTitle = modal.querySelector('[data-store-head-title]');
    var headMeta = modal.querySelector('[data-store-head-meta]');
    var headBadge = modal.querySelector('[data-store-head-badge]');
    var grid = modal.querySelector('[data-store-grid]');

    var closeModal = function () {
      if (HAS_GSAP && self.allowMotion) {
        GS.to(panel, {
          y: 24,
          autoAlpha: 0,
          scale: 0.98,
          duration: 0.2,
          ease: 'power2.in',
          onComplete: function () {
            if (typeof modal.close === 'function') {
              try { modal.close(); } catch (e) {}
            }
            modal.removeAttribute('open');
            GS.set(panel, { clearProps: 'all' });
          }
        });
      } else {
        if (typeof modal.close === 'function') {
          try { modal.close(); } catch (e) {}
        }
        modal.removeAttribute('open');
      }
    };

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target.classList.contains('hp-store-modal')) {
        closeModal();
      }
    });

    var triggerOpen = function (storeEl) {
      var sid = storeEl.dataset.storeId;
      var name = storeEl.dataset.storeName || 'Store';
      var unit = storeEl.dataset.storeUnit || '';
      var floor = storeEl.dataset.storeFloor || '';
      var category = storeEl.dataset.storeCategory || '';
      var deals = storeEl.dataset.storeDeals || '4';
      var discount = storeEl.dataset.storeDiscount || 'Up to 50% OFF';
      var tone = storeEl.dataset.storeTone || '#7C3AED';

      if (headUnit) {
        headUnit.textContent = unit;
        headUnit.style.background = tone;
      }
      if (headTitle) headTitle.textContent = name + ' Deals';
      if (headMeta) headMeta.textContent = floor + (category ? ' · ' + category : '') + ' · ' + deals + ' Active Deals';
      if (headBadge) headBadge.textContent = discount;

      panel.style.setProperty('--tone', tone);

      var dataScript = document.getElementById('hp-store-data-' + sid);
      var products = [];
      if (dataScript) {
        try {
          products = JSON.parse(dataScript.textContent || '[]');
        } catch (err) {
          console.warn('Failed to parse store products', err);
        }
      }

      var html = '';
      if (products && products.length) {
        products.forEach(function (p) {
          var pBadge = p.badge ? '<span class="hp-store-card-badge">' + p.badge + '</span>' : '';
          var pDisc = p.discount ? '<span class="hp-store-card-discount">' + p.discount + '</span>' : '';
          var pCompare = p.compare ? '<s>' + p.compare + '</s>' : '';
          var pUrl = p.url || '#';

          html +=
            '<div class="hp-store-card">' +
              '<a href="' + pUrl + '" class="hp-store-card-img-link">' +
                pBadge +
                pDisc +
                '<img src="' + p.image + '" alt="' + p.title + '" loading="lazy" onerror="this.src=\'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=600&auto=format&fit=crop&q=80\'">' +
              '</a>' +
              '<div class="hp-store-card-body">' +
                '<div>' +
                  '<h3 class="hp-store-card-title">' +
                    '<a href="' + pUrl + '">' + p.title + '</a>' +
                  '</h3>' +
                  '<div class="hp-store-card-price">' +
                    '<b>' + p.price + '</b>' +
                    pCompare +
                  '</div>' +
                '</div>' +
                '<a href="' + pUrl + '" class="hp-store-card-cta">' +
                  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
                  'Buy at Counter (Store Pass)' +
                '</a>' +
              '</div>' +
            '</div>';
        });
      } else {
        html = '<div style="grid-column: 1 / -1; padding: 30px; text-align: center; color: var(--muted);">No deals found for this store.</div>';
      }

      grid.innerHTML = html;

      if (typeof modal.showModal === 'function') {
        try {
          modal.showModal();
        } catch (err) {
          modal.setAttribute('open', '');
        }
      } else {
        modal.setAttribute('open', '');
      }
      modal.setAttribute('open', '');
      panel.scrollTop = 0;

      if (HAS_GSAP && self.allowMotion) {
        GS.fromTo(panel, 
          { y: 36, autoAlpha: 0, scale: 0.97 }, 
          { y: 0, autoAlpha: 1, scale: 1, duration: 0.35, ease: 'power3.out' }
        );
        var cards = panel.querySelectorAll('.hp-store-card');
        if (cards.length) {
          GS.fromTo(cards,
            { y: 20, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.3, stagger: 0.04, ease: 'back.out(1.2)', delay: 0.06 }
          );
        }
      }
    };

    this.root.addEventListener('click', function (e) {
      var storeEl = e.target.closest('[data-store-open]');
      if (!storeEl) return;
      e.preventDefault();
      triggerOpen(storeEl);
    });

    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        var storeEl = e.target.closest('[data-store-open]');
        if (storeEl) {
          e.preventDefault();
          triggerOpen(storeEl);
        }
      }
    });
  };

  /* ----------------------------------------------------------- CHIP NAV
     Chips are real anchor links, so they still work with JavaScript disabled.

     The highlight is deliberately NOT one ScrollTrigger per chip. Doing that
     let several sections satisfy their own active range at once, so two or
     three chips lit up together and the colour told the visitor nothing. Here
     a single scroll handler picks exactly one winner - the last section whose
     top has passed the sticky bar - and every other chip is cleared. Clicking
     a chip marks it immediately and locks the spy briefly, so the highlight
     never disagrees with what the visitor just tapped. */
  MallSection.prototype.bindChips = function () {
    var self = this;
    var bar = this.$('.hp-chips');
    var chips = this.$$('.hp-chip[href^="#"]');
    if (!chips.length) return;

    var targets = chips.map(function (chip) {
      return { chip: chip, el: document.getElementById(chip.getAttribute('href').slice(1)) };
    }).filter(function (t) { return t.el; });
    if (!targets.length) return;

    this.chipLock = 0;

    /* Exactly one chip carries aria-current at any moment. */
    function setActive(chip) {
      if (self.activeChip === chip) return;
      chips.forEach(function (c) { c.setAttribute('aria-current', c === chip ? 'true' : 'false'); });
      self.activeChip = chip;
      /* Keep the active chip visible inside the horizontally scrolling bar */
      if (chip && bar) {
        var wrap = bar.querySelector('.hp-wrap');
        if (wrap && wrap.scrollWidth > wrap.clientWidth) {
          var left = chip.offsetLeft - (wrap.clientWidth - chip.offsetWidth) / 2;
          wrap.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      }
    }
    this.setActiveChip = setActive;

    function spy() {
      if (Date.now() < self.chipLock) return;
      var offset = (parseFloat(getComputedStyle(self.root).getPropertyValue('--bars-h')) || 0) + 24;
      var winner = null;
      targets.forEach(function (t) {
        if (t.el.getBoundingClientRect().top - offset <= 1) winner = t.chip;
      });
      /* Above the first section, nothing is active - better than lying. */
      setActive(winner);
    }
    this.chipSpy = spy;

    chips.forEach(function (t) {
      t.addEventListener('click', function (e) {
        var el = document.getElementById(t.getAttribute('href').slice(1));
        if (!el) return;
        e.preventDefault();
        setActive(t);
        /* Hold the spy off while the smooth scroll is in flight, otherwise the
           sections passing under the bar would steal the highlight. */
        self.chipLock = Date.now() + 900;
        var offset = (parseFloat(getComputedStyle(self.root).getPropertyValue('--bars-h')) || 0) + 12;
        var y = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        if (history.replaceState) history.replaceState(null, '', t.getAttribute('href'));
      });
    });

    this.onScroll = function () {
      if (self.raf) return;
      self.raf = requestAnimationFrame(function () { self.raf = 0; spy(); self.updateProgress(); });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    spy();
  };

  /* ------------------------------------------------- PROGRESS + BACK TO TOP */
  MallSection.prototype.updateProgress = function () {
    var bar = this.$('[data-progress] span');
    var top = this.$('[data-totop]');
    var rect = this.root.getBoundingClientRect();
    var total = rect.height - window.innerHeight;
    if (bar) {
      var p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      bar.style.transform = 'scaleX(' + p + ')';
      bar.style.width = '100%';
    }
    if (top) top.classList.toggle('hp-on', -rect.top > window.innerHeight * 0.8);
  };

  MallSection.prototype.bindBackToTop = function () {
    var btn = this.$('[data-totop]');
    if (!btn) return;
    var self = this;
    btn.addEventListener('click', function () {
      window.scrollTo({ top: self.root.getBoundingClientRect().top + window.pageYOffset, behavior: 'smooth' });
    });
  };

  /* ------------------------------------------------------------------ FAQ
     Built on native <details>, so it opens and closes with no JavaScript and
     is keyboard accessible for free. This only adds the accordion behaviour:
     opening one answer closes the others. */
  MallSection.prototype.bindFaq = function () {
    var groups = this.$$('[data-faq]');
    groups.forEach(function (group) {
      var items = Array.prototype.slice.call(group.querySelectorAll('details'));
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          items.forEach(function (other) { if (other !== d) other.open = false; });
          if (HAS_GSAP) ST.refresh();
        });
      });
    });
  };

  /* --------------------------------------------------------------- MOTION */
  MallSection.prototype.startMotion = function () {
    var self = this;
    var run = function () {
      self.allowMotion = true;
      self.animateHero();
      self.animateContent();
      self.layoutMarquees();
    };
    var stop = function () {
      self.allowMotion = false;
      self.killTriggers(self.heroTriggers);
      self.killTriggers(self.contentTriggers);
      self.marquees.forEach(function (m) {
        if (m.tween) { m.tween.kill(); m.tween = null; }
        GS.set(m.track, { clearProps: 'transform' });
      });
    };
    /* gsap.matchMedia() needs window.matchMedia. Where it is missing we start
       motion directly rather than let the call throw and take the rest of the
       boot sequence with it. */
    if (typeof window.matchMedia === 'function') {
      this.mm = GS.matchMedia();
      this.mm.add('(prefers-reduced-motion: no-preference)', function () { run(); return stop; });
    } else {
      run();
    }
    window.addEventListener('load', function () { ST.refresh(); });
  };

  MallSection.prototype.animateHero = function () {
    var self = this;
    var hero = this.$('.hp-hero');
    if (hero) {
      var tl = GS.timeline({ defaults: { duration: .9, ease: 'power3.out' } });
      var bits = this.$$('[data-hero]');
      var shots = this.$$('[data-shot]');
      var tag = this.$('[data-tag]');
      if (bits.length) tl.from(bits, { y: 26, autoAlpha: 0, stagger: .09 });
      if (shots.length) tl.from(shots, { y: 44, autoAlpha: 0, scale: .94, stagger: .11 }, '-=.65');
      if (tag) tl.from(tag, { rotation: -14, autoAlpha: 0, transformOrigin: '50% 0', duration: 1.1, ease: 'elastic.out(1,.55)' }, '-=.5');

      shots.forEach(function (shot) {
        self.heroTriggers.push(GS.to(shot, {
          y: function () { return parseFloat(shot.dataset.depth) || 0; },
          ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: .6 }
        }).scrollTrigger);
      });
    }

    /* Count any [data-count] up when it scrolls into view */
    this.$$('[data-count]').forEach(function (n) {
      var target = parseFloat(n.dataset.count);
      if (isNaN(target)) return;
      var o = { v: 0 };
      self.heroTriggers.push(ST.create({
        trigger: n, start: 'top 92%', once: true,
        onEnter: function () {
          GS.to(o, {
            v: target, duration: 1.6, ease: 'power2.out',
            onUpdate: function () { n.textContent = Math.round(o.v).toLocaleString(); }
          });
        }
      }));
    });
  };

  MallSection.prototype.animateContent = function () {
    var self = this;
    ST.refresh();

    this.$$('[data-reveal]').forEach(function (n) {
      self.contentTriggers.push(GS.from(n, {
        y: 28, autoAlpha: 0, duration: .8,
        scrollTrigger: { trigger: n, start: 'top 88%', once: true }
      }).scrollTrigger);
    });

    /* Cards arrive a row at a time, with a stagger inside each batch */
    var cards = this.$$('.hp-card, .hp-tile');
    if (cards.length) {
      this.contentTriggers = this.contentTriggers.concat(
        ST.batch(cards, {
          start: 'top 92%', once: true,
          onEnter: function (batch) {
            GS.from(batch, { y: 34, autoAlpha: 0, duration: .7, stagger: .07, ease: 'power3.out', overwrite: true });
          }
        })
      );
    }

    this.$$('[data-parallax]').forEach(function (img) {
      var host = img.closest('.hp-ad, .hp-split-media');
      if (!host) return;
      self.contentTriggers.push(GS.fromTo(img, { yPercent: -7 }, {
        yPercent: 7, ease: 'none',
        scrollTrigger: { trigger: host, start: 'top bottom', end: 'bottom top', scrub: .5 }
      }).scrollTrigger);
    });
  };

  /* ------------------------------------------------------------ LIFECYCLE */
  function mount(root) {
    if (!root || instances[root.dataset.sectionId]) return;
    instances[root.dataset.sectionId] = new MallSection(root);
  }
  function unmount(id) {
    if (instances[id]) { instances[id].destroy(); delete instances[id]; }
  }
  function mountAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mall-section]'), mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
  else mountAll();

  /* Theme editor: rebuild the instance whenever the merchant edits it, so
     newly added blocks get their animations, clocks and marquees. */
  document.addEventListener('shopify:section:load', function (e) {
    unmount(e.detail.sectionId);
    mount(e.target.querySelector('[data-mall-section]'));
  });
  document.addEventListener('shopify:section:unload', function (e) { unmount(e.detail.sectionId); });
  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (block && block.scrollIntoView) block.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();
